<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { pauseRulesAPI } from 'dashboard/api/talkviaAgentPauses';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const copy = {
  title: 'Status de disponibilidade',
  subtitle:
    'Cadastre as pausas dos agentes e configure a mensagem enviada automaticamente aos clientes durante a indisponibilidade.',
  new: 'Nova regra',
  save: 'Salvar regra',
  cancel: 'Cancelar',
  edit: 'Editar',
  delete: 'Excluir',
  active: 'Ativo',
  inactive: 'Inativo',
  autoReplyBadge: 'Resposta automática',
  empty:
    'Nenhuma regra de pausa cadastrada. Crie a primeira para liberar o recurso de pausa para os agentes.',
  fields: {
    name: 'Nome da pausa',
    icon: 'Ícone da pausa',
    description: 'Descrição (opcional)',
    active: 'Regra ativa',
    activeHint:
      'Disponibiliza esta opção no menu de disponibilidade dos agentes.',
    autoReply: 'Enviar resposta automática',
    autoReplyHint:
      'Responde uma vez por conversa durante cada sessão desta pausa.',
    autoReplyMessage: 'Mensagem para o cliente',
  },
  placeholders: {
    name: 'Ex.: Almoço, Banheiro, Reunião',
    description: 'Explique quando esta pausa deve ser usada.',
    autoReplyMessage:
      'No momento, nosso atendente está indisponível. Assim que possível, retornaremos seu contato.',
  },
  messages: {
    deleteTitle: 'Excluir regra de pausa',
    saved: 'Regra salva.',
    deleted: 'Regra excluída.',
    saveFailed: 'Não foi possível salvar.',
    deleteFailed: 'Não foi possível excluir.',
    loadFailed: 'Não foi possível carregar as regras.',
    confirmDelete: 'Excluir a regra "{name}"?',
    available: 'A alteração já está disponível para os agentes.',
  },
};

const DEFAULT_PAUSE_ICON = 'i-lucide-coffee';
const iconOptions = [
  { value: 'i-lucide-coffee', label: 'Café' },
  { value: 'i-lucide-utensils', label: 'Refeição' },
  { value: 'i-lucide-users', label: 'Reunião' },
  { value: 'i-lucide-presentation', label: 'Treinamento' },
  { value: 'i-lucide-phone', label: 'Ligação' },
  { value: 'i-lucide-clock-3', label: 'Intervalo' },
  { value: 'i-lucide-moon', label: 'Descanso' },
  { value: 'i-lucide-briefcase-medical', label: 'Saúde' },
];

const normalizedText = value => String(value ?? '').trim();

const errorMessage = error => {
  const apiError = error?.response?.data?.error;

  if (typeof apiError === 'string' && apiError.trim()) return apiError;
  if (apiError && typeof apiError === 'object') {
    const details = Object.values(apiError).flat().filter(Boolean).join(', ');
    if (details) return details;
  }

  if (error?.response?.status === 403) {
    return 'Somente administradores podem alterar as regras de pausa.';
  }

  if (!error?.response && error?.message) {
    return `${copy.messages.saveFailed} ${error.message}`;
  }

  return copy.messages.saveFailed;
};

const rules = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const isDeleting = ref(false);
const editingId = ref(null);
const ruleDialogRef = ref(null);
const deleteDialogRef = ref(null);
const feedbackDialogRef = ref(null);
const ruleToDelete = ref(null);
const feedback = reactive({ title: '', description: '', kind: 'success' });
const form = reactive({
  name: '',
  icon: DEFAULT_PAUSE_ICON,
  description: '',
  active: true,
  autoReplyEnabled: false,
  autoReplyMessage: '',
});

const dialogTitle = computed(() =>
  editingId.value ? 'Editar regra de pausa' : 'Criar regra de pausa'
);
const canSave = computed(
  () =>
    Boolean(normalizedText(form.name)) &&
    (!form.autoReplyEnabled || Boolean(normalizedText(form.autoReplyMessage)))
);

const usageCountLabel = count => `${count} uso(s)`;

const resetForm = () => {
  editingId.value = null;
  Object.assign(form, {
    name: '',
    icon: DEFAULT_PAUSE_ICON,
    description: '',
    active: true,
    autoReplyEnabled: false,
    autoReplyMessage: '',
  });
};

const openNew = () => {
  resetForm();
  ruleDialogRef.value?.open();
};

const openEdit = rule => {
  editingId.value = rule.id;
  Object.assign(form, {
    name: rule.name,
    icon: rule.icon || DEFAULT_PAUSE_ICON,
    description: rule.description || '',
    active: rule.active,
    autoReplyEnabled: rule.auto_reply_enabled,
    autoReplyMessage: rule.auto_reply_message || '',
  });
  ruleDialogRef.value?.open();
};

const showFeedback = (title, description, kind = 'error') => {
  feedback.title = title;
  feedback.description = description;
  feedback.kind = kind;
  feedbackDialogRef.value?.open();
};

const closeFeedback = () => feedbackDialogRef.value?.close();

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await pauseRulesAPI.get();
    rules.value = data || [];
  } catch {
    showFeedback('Não foi possível carregar', copy.messages.loadFailed);
  } finally {
    isLoading.value = false;
  }
};

const save = async () => {
  if (!canSave.value) return;

  isSaving.value = true;
  try {
    const payload = {
      name: normalizedText(form.name),
      icon: form.icon,
      description: normalizedText(form.description),
      active: form.active,
      auto_reply_enabled: form.autoReplyEnabled,
      auto_reply_message: normalizedText(form.autoReplyMessage),
    };
    if (editingId.value) {
      await pauseRulesAPI.update(editingId.value, payload);
    } else {
      await pauseRulesAPI.create(payload);
    }
    ruleDialogRef.value?.close();
    await load();
    showFeedback('Regra salva', copy.messages.saved, 'success');
  } catch (error) {
    showFeedback('Não foi possível salvar', errorMessage(error));
  } finally {
    isSaving.value = false;
  }
};

const openDeleteDialog = rule => {
  ruleToDelete.value = rule;
  deleteDialogRef.value?.open();
};

const deleteRule = async () => {
  if (!ruleToDelete.value) return;

  isDeleting.value = true;
  try {
    await pauseRulesAPI.delete(ruleToDelete.value.id);
    deleteDialogRef.value?.close();
    ruleToDelete.value = null;
    await load();
    showFeedback('Regra excluída', copy.messages.deleted, 'success');
  } catch {
    showFeedback('Não foi possível excluir', copy.messages.deleteFailed);
  } finally {
    isDeleting.value = false;
  }
};

const toggleActive = async rule => {
  try {
    await pauseRulesAPI.update(rule.id, {
      name: rule.name,
      icon: rule.icon || DEFAULT_PAUSE_ICON,
      description: rule.description || '',
      active: !rule.active,
      auto_reply_enabled: rule.auto_reply_enabled,
      auto_reply_message: rule.auto_reply_message || '',
    });
    await load();
  } catch {
    showFeedback('Não foi possível salvar', copy.messages.saveFailed);
  }
};

onMounted(load);
</script>

<template>
  <div class="flex w-full max-w-2xl flex-col ltr:mr-auto rtl:ml-auto">
    <BaseSettingsHeader :title="copy.title" :description="copy.subtitle" />

    <div class="mt-6 flex flex-col gap-5">
      <div class="flex justify-end">
        <Button
          :label="copy.new"
          icon="i-lucide-plus"
          color="blue"
          @click="openNew"
        />
      </div>

      <div v-if="isLoading" class="flex items-center justify-center py-10">
        <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-10" />
      </div>

      <div
        v-else-if="!rules.length"
        class="flex flex-col items-center gap-3 rounded-xl border border-dashed border-n-weak py-12 text-center"
      >
        <span class="i-lucide-coffee size-10 text-n-slate-7" />
        <p class="max-w-xs text-sm text-n-slate-11">{{ copy.empty }}</p>
        <Button
          :label="copy.new"
          icon="i-lucide-plus"
          color="blue"
          @click="openNew"
        />
      </div>

      <div
        v-else
        class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-2"
      >
        <div
          v-for="(rule, index) in rules"
          :key="rule.id"
          class="flex items-start justify-between gap-3 px-5 py-4 transition-colors hover:bg-n-alpha-1"
          :class="index < rules.length - 1 ? 'border-b border-n-weak' : ''"
        >
          <div class="flex min-w-0 flex-1 items-start gap-3">
            <button
              type="button"
              class="grid size-10 shrink-0 place-items-center rounded-full border-2 transition-colors"
              :class="
                rule.active
                  ? 'border-n-brand/40 bg-n-brand'
                  : 'border-n-weak bg-n-alpha-1'
              "
              :title="
                rule.active ? 'Clique para desativar' : 'Clique para ativar'
              "
              @click="toggleActive(rule)"
            >
              <span
                class="size-4"
                :class="[
                  rule.icon || DEFAULT_PAUSE_ICON,
                  rule.active ? 'text-white' : 'text-n-slate-10',
                ]"
              />
            </button>

            <div class="min-w-0">
              <p class="text-sm font-semibold text-n-slate-12">
                {{ rule.name }}
              </p>
              <p
                v-if="rule.description"
                class="mt-0.5 text-xs leading-relaxed text-n-slate-10"
              >
                {{ rule.description }}
              </p>
              <div class="mt-1.5 flex items-center gap-2">
                <span
                  class="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium"
                  :class="
                    rule.active
                      ? 'border-n-brand/30 bg-n-brand/10 text-n-brand'
                      : 'border-n-weak bg-n-alpha-1 text-n-slate-10'
                  "
                >
                  <span
                    class="size-1.5 rounded-full"
                    :class="rule.active ? 'bg-n-brand' : 'bg-n-slate-7'"
                  />
                  {{ rule.active ? copy.active : copy.inactive }}
                </span>
                <span
                  v-if="rule.sessions_count"
                  class="text-[10px] text-n-slate-10"
                >
                  {{ usageCountLabel(rule.sessions_count) }}
                </span>
                <span
                  v-if="rule.auto_reply_enabled"
                  class="inline-flex items-center gap-1 rounded-full border border-n-blue-6 bg-n-blue-3 px-2 py-0.5 text-[10px] font-medium text-n-blue-11"
                >
                  <span class="i-lucide-message-circle-reply size-3" />
                  {{ copy.autoReplyBadge }}
                </span>
              </div>
            </div>
          </div>

          <div class="flex shrink-0 items-center gap-1">
            <Button
              :label="copy.edit"
              icon="i-lucide-pencil"
              size="sm"
              color="slate"
              variant="ghost"
              @click="openEdit(rule)"
            />
            <Button
              :label="copy.delete"
              icon="i-lucide-trash-2"
              size="sm"
              color="ruby"
              variant="faded"
              @click="openDeleteDialog(rule)"
            />
          </div>
        </div>
      </div>
    </div>

    <Dialog
      ref="ruleDialogRef"
      width="xl"
      position="top"
      :title="dialogTitle"
      description="Defina como esta opção será exibida no menu de disponibilidade."
      :cancel-button-label="copy.cancel"
      :confirm-button-label="copy.save"
      :is-loading="isSaving"
      :disable-confirm-button="!canSave"
      overflow-y-auto
      sticky-header-footer
      @confirm="save"
      @close="resetForm"
    >
      <div class="flex flex-col gap-4 py-0.5 sm:gap-5">
        <Input
          v-model="form.name"
          :label="copy.fields.name"
          :placeholder="copy.placeholders.name"
          autofocus
        />

        <div>
          <p class="mb-2 text-sm font-medium text-n-slate-12">
            {{ copy.fields.icon }}
          </p>
          <div class="grid grid-cols-4 gap-2 sm:grid-cols-8">
            <button
              v-for="option in iconOptions"
              :key="option.value"
              type="button"
              class="flex min-w-0 flex-col items-center gap-1 rounded-lg border p-2 transition-colors"
              :class="
                form.icon === option.value
                  ? 'border-n-blue-8 bg-n-blue-3 text-n-blue-11'
                  : 'border-n-weak bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
              "
              :title="option.label"
              @click="form.icon = option.value"
            >
              <span class="size-5" :class="option.value" />
              <span class="w-full truncate text-center text-[10px]">
                {{ option.label }}
              </span>
            </button>
          </div>
        </div>

        <TextArea
          v-model="form.description"
          :label="copy.fields.description"
          :placeholder="copy.placeholders.description"
          auto-height
          min-height="4.5rem"
          max-height="8rem"
          resize
        />

        <div class="grid gap-3 sm:grid-cols-2">
          <div
            class="flex items-start justify-between gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3 sm:p-4"
          >
            <div class="min-w-0">
              <p class="text-sm font-medium text-n-slate-12">
                {{ copy.fields.active }}
              </p>
              <p class="mt-1 text-xs leading-5 text-n-slate-10">
                {{ copy.fields.activeHint }}
              </p>
            </div>
            <Switch v-model="form.active" class="mt-0.5 shrink-0" />
          </div>

          <div
            class="flex items-start justify-between gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3 sm:p-4"
          >
            <div class="min-w-0">
              <p class="text-sm font-medium text-n-slate-12">
                {{ copy.fields.autoReply }}
              </p>
              <p class="mt-1 text-xs leading-5 text-n-slate-10">
                {{ copy.fields.autoReplyHint }}
              </p>
            </div>
            <Switch v-model="form.autoReplyEnabled" class="mt-0.5 shrink-0" />
          </div>
        </div>

        <TextArea
          v-if="form.autoReplyEnabled"
          v-model="form.autoReplyMessage"
          :label="copy.fields.autoReplyMessage"
          :placeholder="copy.placeholders.autoReplyMessage"
          :max-length="2000"
          show-character-count
          auto-height
          min-height="6rem"
          max-height="10rem"
          resize
        />
      </div>
    </Dialog>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="copy.messages.deleteTitle"
      :description="
        copy.messages.confirmDelete.replace('{name}', ruleToDelete?.name || '')
      "
      :cancel-button-label="copy.cancel"
      :confirm-button-label="copy.delete"
      :is-loading="isDeleting"
      @confirm="deleteRule"
      @close="ruleToDelete = null"
    />

    <Dialog
      ref="feedbackDialogRef"
      :type="feedback.kind === 'error' ? 'alert' : 'edit'"
      width="md"
      :title="feedback.title"
      confirm-button-label="Entendi"
      :show-cancel-button="false"
      @confirm="closeFeedback"
    >
      <div
        class="flex items-start gap-3 rounded-xl border p-4"
        :class="
          feedback.kind === 'error'
            ? 'border-n-ruby-6 bg-n-ruby-3 text-n-ruby-11'
            : 'border-n-brand/25 bg-n-brand/10 text-n-slate-12'
        "
      >
        <span
          class="grid size-9 shrink-0 place-items-center rounded-full"
          :class="
            feedback.kind === 'error'
              ? 'bg-n-ruby-9/15 text-n-ruby-11'
              : 'bg-n-brand/15 text-n-brand'
          "
        >
          <span
            class="size-4.5"
            :class="
              feedback.kind === 'error'
                ? 'i-lucide-triangle-alert'
                : 'i-lucide-circle-check'
            "
          />
        </span>
        <div class="min-w-0">
          <p class="m-0 text-sm leading-6">{{ feedback.description }}</p>
          <p
            v-if="feedback.kind === 'success'"
            class="m-0 mt-1 text-xs text-n-slate-10"
          >
            {{ copy.messages.available }}
          </p>
        </div>
      </div>
    </Dialog>
  </div>
</template>
